import { WORLD_H, WORLD_W } from '../constants.js';

/**
 * Terrain truth is a flat alpha grid: 1 = solid, 0 = empty.
 * DOM-free so the Node server and the browser client share it exactly.
 */

export function createTerrain(fill = 0): Uint8Array {
  const t = new Uint8Array(WORLD_W * WORLD_H);
  if (fill) t.fill(fill);
  return t;
}

export function terrainIdx(x: number, y: number): number {
  return y * WORLD_W + x;
}

export function inBounds(x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < WORLD_W && y < WORLD_H;
}

export function isSolid(terrain: Uint8Array, x: number, y: number): boolean {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  if (!inBounds(ix, iy)) return false;
  return terrain[iy * WORLD_W + ix] === 1;
}

/** Solid test at integer coordinates (no flooring). */
export function isSolidPx(terrain: Uint8Array, ix: number, iy: number): boolean {
  if (!inBounds(ix, iy)) return false;
  return terrain[iy * WORLD_W + ix] === 1;
}

/**
 * Identity seed for a crater's rim jitter — derived ONLY from the circle's
 * (rounded) position and radius. Every machine (server, client peer, fork,
 * resync replay) rasterizes the same crater to the same pixels regardless of
 * call order or how much RNG history each sim has consumed. Quantizing to
 * whole pixels absorbs cross-engine float noise (~1e-9 px), which would
 * otherwise flip individual jitter bits between browsers.
 */
function circleSeed(cx: number, cy: number, r: number): number {
  const h =
    Math.imul(Math.round(cx) | 0, 73856093) ^
    Math.imul(Math.round(cy) | 0, 19349663) ^
    Math.imul(Math.round(r) | 0, 83492791);
  return (h ^ (h >>> 13)) >>> 0;
}

/** Local mulberry32 — never touches a sim's rng stream. */
function nextJitter(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Zero every pixel inside a circle. The rim gets deterministic organic
 * jitter (16 radial factors interpolated by angle) seeded from the circle
 * ITSELF — see `circleSeed`. Lockstep identity: two machines carving the
 * same crater end with byte-identical masks, so projectile collisions after
 * the blast match everywhere. (Was rng-stream-seeded: each machine drew from
 * a different stream position — visRng vs sim rng vs replay — and the masks
 * drifted apart, compounding into the cross-device divergence we saw.)
 */
export function carveCircle(
  terrain: Uint8Array,
  cx: number,
  cy: number,
  r: number,
): void {
  if (r <= 0) return;
  const rand = nextJitter(circleSeed(cx, cy, r));
  const jitter = new Float32Array(17);
  for (let i = 0; i < 16; i++) jitter[i] = 0.9 + rand() * 0.2;
  jitter[16] = jitter[0];

  const x0 = Math.max(0, Math.floor(cx - r - 2));
  const x1 = Math.min(WORLD_W - 1, Math.ceil(cx + r + 2));
  const y0 = Math.max(0, Math.floor(cy - r - 2));
  const y1 = Math.min(WORLD_H - 1, Math.ceil(cy + r + 2));

  for (let y = y0; y <= y1; y++) {
    const dy = y + 0.5 - cy;
    for (let x = x0; x <= x1; x++) {
      const dx = x + 0.5 - cx;
      const d = Math.hypot(dx, dy);
      const ang = Math.atan2(dy, dx); // -PI..PI
      const fi = ((ang + Math.PI) / (Math.PI * 2)) * 16;
      const i0 = Math.min(15, Math.floor(fi)); // +PI wraps to the seam
      const f = fi - i0;
      const eff = r * (jitter[i0] * (1 - f) + jitter[i0 + 1] * f);
      if (d <= eff) terrain[y * WORLD_W + x] = 0;
    }
  }
}

/** Fill a disc with solid pixels (used by map generation). */
export function fillCircle(terrain: Uint8Array, cx: number, cy: number, r: number): void {
  const x0 = Math.max(0, Math.floor(cx - r));
  const x1 = Math.min(WORLD_W - 1, Math.ceil(cx + r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const y1 = Math.min(WORLD_H - 1, Math.ceil(cy + r));
  const r2 = r * r;
  for (let y = y0; y <= y1; y++) {
    const dy = y + 0.5 - cy;
    for (let x = x0; x <= x1; x++) {
      const dx = x + 0.5 - cx;
      if (dx * dx + dy * dy <= r2) terrain[y * WORLD_W + x] = 1;
    }
  }
}

/** DDA line sample — true if ANY pixel along the segment is solid. */
export function solidLine(
  terrain: Uint8Array,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): boolean {
  return lineHit(terrain, x0, y0, x1, y1) !== null;
}

/**
 * First solid point along a segment (≈1px sampling), or null.
 * This is what prevents fast projectiles from tunneling thin walls.
 */
export function lineHit(
  terrain: Uint8Array,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): { x: number; y: number } | null {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const steps = Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)));
  if (steps <= 0) return isSolid(terrain, x0, y0) ? { x: x0, y: y0 } : null;
  const sx = dx / steps;
  const sy = dy / steps;
  let x = x0;
  let y = y0;
  for (let i = 0; i <= steps; i++) {
    if (isSolid(terrain, x, y)) return { x, y };
    x += sx;
    y += sy;
  }
  return null;
}

/** First solid y scanning downward from fromY; -1 when nothing below. */
export function surfaceY(terrain: Uint8Array, x: number, fromY = 0): number {
  const ix = Math.floor(x);
  if (ix < 0 || ix >= WORLD_W) return -1;
  const start = Math.max(0, Math.floor(fromY));
  for (let y = start; y < WORLD_H; y++) {
    if (terrain[y * WORLD_W + ix] === 1) return y;
  }
  return -1;
}

/**
 * Surface normal (unit, pointing from solid toward empty) estimated with
 * central differences of solidity around (x, y).
 */
export function surfaceNormal(
  terrain: Uint8Array,
  x: number,
  y: number,
  radius = 4,
): { x: number; y: number } {
  const s = (px: number, py: number): number => (isSolid(terrain, px, py) ? 1 : 0);
  const gx = s(x + radius, y) - s(x - radius, y);
  const gy = s(x, y + radius) - s(x, y - radius);
  const len = Math.hypot(gx, gy);
  if (len < 1e-6) return { x: 0, y: -1 };
  return { x: -gx / len, y: -gy / len };
}

/** FNV-1a hash over the whole grid — determinism assertions in tests. */
export function hashTerrain(terrain: Uint8Array): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < terrain.length; i++) {
    h ^= terrain[i];
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
